import { useState, useEffect } from 'react';
import Grid from './overview/Grid';
import IncidentList from './overview/IncidentList';
import Filter from './overview/Filter';
import Bar from './overview/Bar';
import Parser from '../rz-status_parser';
import { ICategory, IGlobalMessage, IIncident, IMaintanance, IServiceTree, ServiceState } from '../rz-status_models';
import { FilterContext } from './contextProviders/FilterContext';
import { CollapseContext } from './contextProviders/CollapseContext';
import Constants from '../rz-status_constants';
import StatusMobil from './overview/StatusMobil';
import NavLinks from './overview/NavLinks';
import Notifications from './listview/notifications';

type LoadErrors = {
  maintenances: boolean;
  incidents: boolean;
  categories: boolean;
  messages: boolean;
};

export default function() {
  const [tick, setTick] = useState(0);
  const parser = new Parser()
  
  const [treeWithState, setTreeWithState] = useState<ICategory[] | null>();
  const [counts, setCounts] = useState<Record<ServiceState, number>>({});
  const [filter, setFilter] = useState<ServiceState[]>(() => {
    const saved = localStorage.getItem("filter");
    if(saved != null)
      return JSON.parse(saved);
    return [ServiceState.Available, ServiceState.Maintanance, ServiceState.Limited, ServiceState.Outage]
  });

  const [expanded, setExpanded] = useState<number[]>([]);
  const [allExpanded, setAllExpanded] = useState<number[]>([]);

  const [data, setData] = useState<null | IServiceTree>(null);
  const [messages, setMessages] = useState<null | IGlobalMessage[]>(null);
  const [errors, setErrors] = useState<LoadErrors>({
    maintenances: false,
    incidents: false,
    categories: false,
    messages: false,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    localStorage.setItem("filter", JSON.stringify(filter))
  }, [filter]);

  useEffect(() => {
    const fetchText = async (url: string, errorKey: keyof LoadErrors): Promise<string | null> => {
      try {
        const response = await fetch(url);

        if (!response.ok) {
          setErrors(prev => ({
            ...prev,
            [errorKey]: true,
          }));

          return null;
        }

        setErrors(prev => ({
          ...prev,
          [errorKey]: false,
        }));

        return await response.text();
      } catch {
        setErrors(prev => ({
          ...prev,
          [errorKey]: true,
        }));

        return null;
      }
    };

    const load = async () => {
      // Maintenances
      const maintenanceJson = await fetchText(Constants.Paths.maintananceJsonPath, "maintenances");
      const maintenances: IMaintanance[] | null = maintenanceJson ? parser.parseMaintanances(maintenanceJson, true) : null;

      // Incidents
      const incidentsJson = await fetchText(Constants.Paths.currentIncidentsJsonPath, "incidents");
      const incidents: IIncident[] | null = incidentsJson ? parser.parseIncidents(incidentsJson) : null;

      // Categories
      const servicesTreeJson = await fetchText(Constants.Paths.servicesTreeJsonPath, "categories");

      if (!servicesTreeJson) {
        if(!incidents)
          setData(null);
        else
          setData(parser.parseCategories("{}", incidents, maintenances));
      }
      else {
        setData(parser.parseCategories(
          servicesTreeJson,
          incidents,
          maintenances,
        ));
      }

      // Global Messages
      const messagesJson = await fetchText(Constants.Paths.globalmessagesJsonPath, "messages");
      setMessages(messagesJson ? parser.parseNotifications(messagesJson).filter(n => n.text?.trim().length > 0) : null);

      setLoading(false);
    };

    load().catch(() => {
      setErrors(prev => ({
        ...prev,
        services: true,
      }));

      setData(null);
      setLoading(false);
    });

    return () => {};
  }, [tick]);

  useEffect(() => {
      const interval = setInterval(() => {
        setTick(t => t + 1);
      }, Constants.Refresh.intervallInSeconds * 1000);

      return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if(data != null && data.categories != null) {
      createFilteredTree(data.categories);
      setCounts(data.states)
    }
    else {
      if(data == null) 
        setTreeWithState(null);
      else if(data.categories == null || data.categories.length == 0)
        setTreeWithState([]); 
    }
  }, [data, filter])
  
  function createFilteredTree(tree: ICategory[]){
    function recursiveCountAndFilter(category: ICategory) {
      category.services = category.services.filter(service => {
        return filter.includes(service.currentStatus)      
      });

      if(category.categories.length > 0) {
        category.categories = category.categories.filter(c => {
          recursiveCountAndFilter(c)
          return c.services.length > 0 || c.categories.length > 0
        })
      }
    }

    const t = structuredClone(tree).filter(c => {
      recursiveCountAndFilter(c)
      return c.services.length > 0 || c.categories.length > 0
    }).sort(sortCategories)

    setAllExpanded(flattenNames(t))
    setTreeWithState(t)
  }

  function sortCategories(a: ICategory, b: ICategory) {
    const aHas = filter.slice(1).reduce((acc, state) => acc + a.states[state], 0) > 0;
    const bHas = filter.slice(1).reduce((acc, state) => acc + b.states[state], 0) > 0;
    if(aHas) {
      if(bHas)
        return a.name.localeCompare(b.name);
      return -1
    }
    if(bHas){
       return 1
    }
    return a.name.localeCompare(b.name);
  }

  function flattenNames(items: ICategory[]): number[] {
    return items.reduce<number[]>((acc, item) => {
      acc.push(item.id);
      if (item.categories) {
        acc.push(...flattenNames(item.categories));
      }
      return acc;
    }, []);
  }

  function toggle(value: string) {
    setExpanded(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
    );
  };

  function toggleAll() {
    allExpanded.length == expanded.length ? setExpanded([]) : setExpanded(allExpanded)
  }


  return <>
  <div id="top-flex-layout">
    {loading || (errors.categories && !errors.incidents && (messages == null || messages?.length == 0))
      ? <div></div> 
      : (errors.incidents || errors.categories
        ? ((!errors.messages && messages?.length > 0) ? <Notifications messages={messages}/> : <Bar><p>Serviceübersicht aktuell nicht abrufbar</p></Bar>)
        : <Bar data={counts}/>
        )
     }
    <NavLinks />
  </div>

  {(!errors.incidents && !errors.categories) && (!errors.messages && messages?.length > 0) && <Notifications messages={messages}/>}
  {treeWithState ? <>
    <div id="filterbar" className="block block-light collapsible">
      <Filter data={filter} onChange={e => setFilter(e)} disabled={loading || errors.incidents}/>
      {!errors.categories && <button className="button" onClick={toggleAll} aria-label={"Alle Elemente der Serviceübersicht " + ((expanded.length > 0 && allExpanded.length == expanded.length) ? "zuklappen" : "aufklappen")}>{(expanded.length > 0 && allExpanded.length == expanded.length) ? "Alle zuklappen" : "Alle aufklappen"}</button>}
    </div>
    <FilterContext value=>
      <CollapseContext value=>
      {(treeWithState != null && treeWithState.length == 0 && data != null && data.categories?.length == 0 && data.incidents != null) 
      ? <IncidentList empty={data.incidents.length == 0} data={data.incidents.filter(i => (i.currentState == 0 && filter.includes(0) || filter.includes(i.currentState + 1) ))}/>
      : <Grid data={treeWithState} hideState={data != null && data.incidents == null}/>}         
      </CollapseContext>
    </FilterContext>
  </> : (treeWithState == null ? "" : 'Loading...')}</>
}